import { logClientAuditEvent } from '../../project/services/auditTrailApi';
import { ensureAuditSession } from '../../audit/auditSession';
import { getMaterialProjectName } from '../../project/components/dashboard/materials/getMaterialProjectName';
import type { MaterialData } from '../../project/types/materials';
import { isWarehouseMaterialRow } from './warehouseMaterialPayload';

export const PIPE_INVENTORY_AUDIT_MODULE = 'PIPE_INVENTORY';

export type PipeInventoryAuditSnapshot = {
  materialId: number | string | null;
  pipeNRI: string | null;
  pipeNo: string | null;
  pipeHeatNumber: string | null;
  pipePipeId: string | null;
  projectName: string | null;
  warehouseProjectLabel: string | null;
  source: 'warehouse' | 'project';
};

export function pipeInventoryAuditSnapshot(
  row: MaterialData | Partial<MaterialData> | Record<string, unknown>
): PipeInventoryAuditSnapshot {
  const material = row as MaterialData;
  const details = (material.details ?? {}) as Record<string, unknown>;
  const asRecord = row as Record<string, unknown>;
  const warehouse = isWarehouseMaterialRow(asRecord);
  return {
    materialId: material.materialId ?? null,
    pipeNRI: details.pipeNRI != null ? String(details.pipeNRI) : null,
    pipeNo: details.pipeNo != null ? String(details.pipeNo) : null,
    pipeHeatNumber: details.pipeHeatNumber != null ? String(details.pipeHeatNumber) : null,
    pipePipeId: details.pipePipeId != null ? String(details.pipePipeId) : null,
    projectName: getMaterialProjectName(asRecord) || null,
    warehouseProjectLabel:
      asRecord.warehouseProjectLabel != null
        ? String(asRecord.warehouseProjectLabel)
        : details.warehouseProjectLabel != null
          ? String(details.warehouseProjectLabel)
          : null,
    source: warehouse ? 'warehouse' : 'project',
  };
}

function entityIdFromSnapshot(snapshot: PipeInventoryAuditSnapshot): string {
  if (snapshot.materialId != null && snapshot.materialId !== '') {
    return String(snapshot.materialId);
  }
  return snapshot.pipeNRI || snapshot.pipeNo || 'pipe-material';
}

function displayTitleFromSnapshot(snapshot: PipeInventoryAuditSnapshot): string {
  return snapshot.pipeNRI || snapshot.pipeNo || entityIdFromSnapshot(snapshot);
}

function writePipeInventoryAudit(
  eventType: string,
  snapshot: PipeInventoryAuditSnapshot,
  summary: string,
  actionDetails: Record<string, unknown>
): void {
  ensureAuditSession();
  logClientAuditEvent({
    eventType,
    module: PIPE_INVENTORY_AUDIT_MODULE,
    entityType: 'PIPE_MATERIAL',
    entityId: entityIdFromSnapshot(snapshot),
    entityName: displayTitleFromSnapshot(snapshot),
    projectName: snapshot.projectName || snapshot.warehouseProjectLabel || undefined,
    summary,
    displayTitle: displayTitleFromSnapshot(snapshot),
    actionDetails: {
      ...actionDetails,
      snapshot,
    },
  });
}

export function logPipeInventoryMaterialAdded(
  material: MaterialData | Partial<MaterialData> | Record<string, unknown>
): void {
  const snapshot = pipeInventoryAuditSnapshot(material);
  const label = [snapshot.pipeNRI, snapshot.pipeNo].filter(Boolean).join(' / ');
  writePipeInventoryAudit(
    'PIPE_INVENTORY_MATERIAL_ADDED',
    snapshot,
    `Added pipe inventory material${label ? `: ${label}` : ''}.`,
    { operation: 'create' }
  );
}

export function logPipeInventoryMaterialUpdated(
  material: MaterialData | Partial<MaterialData> | Record<string, unknown>
): void {
  const snapshot = pipeInventoryAuditSnapshot(material);
  const label = [snapshot.pipeNRI, snapshot.pipeNo].filter(Boolean).join(' / ');
  writePipeInventoryAudit(
    'PIPE_INVENTORY_MATERIAL_UPDATED',
    snapshot,
    `Updated pipe inventory material${label ? `: ${label}` : ''}.`,
    { operation: 'update' }
  );
}

export function logPipeInventoryMaterialDeleted(
  material: MaterialData | Partial<MaterialData> | Record<string, unknown>
): void {
  const snapshot = pipeInventoryAuditSnapshot(material);
  const label = [snapshot.pipeNRI, snapshot.pipeNo].filter(Boolean).join(' / ');
  writePipeInventoryAudit(
    'PIPE_INVENTORY_MATERIAL_DELETED',
    snapshot,
    `Deleted pipe inventory material${label ? `: ${label}` : ''}.`,
    { operation: 'delete' }
  );
}
